/**
 * Asset Cost-Recovery Report generator.
 *
 * Pick assets and/or asset categories — or switch on "All assets" and/or an
 * ownership scope (Space / a Committee) — plus a reporting period, then
 * Generate a per-asset service statement with Estimated, In-house cost and
 * Actual (recoverable) columns. The Actual total is the amount recoverable from
 * the landlord; in-house cost only reaches it on an asset flagged
 * `is_cost_recoverable` (B5), so the flag is surfaced per asset. CSV and the
 * landlord-ready PDF are produced server-side and downloaded as blobs.
 *
 * Consumes reports/assets/cost_recovery/ (PR1, backend) — see
 * inventory/views.py AssetReportViewSet.cost_recovery.
 */
import { Alert, Badge, Button, Group, Loader, MultiSelect, Paper, SegmentedControl, Select, Stack, Switch, Table, Text, Title } from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { IconAlertTriangle, IconDownload } from '@tabler/icons-react';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { assetsAPI, CostRecoveryOwnershipType, CostRecoveryParams, CostRecoveryPeriod, inventoryAPI, reportsAPI, sigAPI } from '../services/api';
import { Asset, AssetCostRecoveryReport, Category, SIG } from '../types';
import { extractErrorMessage } from '../utils/extractErrorMessage';

type PeriodMode = CostRecoveryPeriod | 'custom';

// "" = Any (no ownership_type param sent).
const OWNERSHIP_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Any', value: '' },
  { label: 'User', value: 'user' },
  { label: 'Group (SIG / committee)', value: 'group' },
  { label: 'Space', value: 'space' },
];

const PERIOD_OPTIONS: Array<{ label: string; value: PeriodMode }> = [
  { label: 'Past week', value: 'past_week' },
  { label: 'Past month', value: 'past_month' },
  { label: 'Past year', value: 'past_year' },
  { label: 'These dates', value: 'custom' },
];

const PERIOD_LABELS: Record<CostRecoveryPeriod, string> = {
  past_week: 'Past week',
  past_month: 'Past month',
  past_year: 'Past year',
};

// Human labels for a service line's origin.
const SOURCE_LABELS: Record<string, string> = {
  pm: 'Internal PM',
  vendor: 'Vendor',
  manual: 'Recorded',
};

const formatMoney = (value: string | number | null): string => {
  const num = typeof value === 'number' ? value : parseFloat(value || '0');
  return Number.isFinite(num) ? num.toFixed(2) : '0.00';
};

const describeWindow = (report: AssetCostRecoveryReport): string =>
  report.period ? PERIOD_LABELS[report.period] : `${report.start_date} → ${report.end_date}`;

// Pre-fill the custom range with the trailing 30 days so switching to "These
// dates" starts from a sensible, immediately-runnable window the user adjusts.
const getDefaultDateRange = (): DatesRangeValue => [
  dayjs().subtract(30, 'day').toDate(),
  new Date(),
];

const AssetCostRecoveryPage: React.FC = () => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [sigs, setSigs] = useState<SIG[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [allAssets, setAllAssets] = useState(false);
  const [ownershipType, setOwnershipType] = useState<string>('');
  const [owningGroup, setOwningGroup] = useState<string>('');
  const [periodMode, setPeriodMode] = useState<PeriodMode>('past_month');
  const [dateRange, setDateRange] = useState<DatesRangeValue>(getDefaultDateRange);

  const [report, setReport] = useState<AssetCostRecoveryReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'csv' | 'pdf' | null>(null);

  // Load the asset + category + committee pickers once. Guard the responses so
  // an undefined payload (e.g. a fully mocked API in tests) can't throw. The
  // committee list reuses the same source as the asset ownership editor.
  useEffect(() => {
    let cancelled = false;
    setLoadingOptions(true);
    Promise.all([
      assetsAPI.listAssets({ page_size: 500 }),
      inventoryAPI.listCategories(),
      sigAPI.listMySIGs(),
    ])
      .then(([assetRes, catRes, sigRes]) => {
        if (cancelled) return;
        setAssets(assetRes?.data?.results ?? []);
        setCategories(catRes?.data?.results ?? []);
        setSigs(sigRes?.data?.results ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(extractErrorMessage(err, 'Failed to load assets and categories.'));
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const assetOptions = useMemo(
    () =>
      assets.map((a) => ({
        value: a.id,
        label: a.asset_tag ? `${a.asset_tag} — ${a.name}` : a.name,
      })),
    [assets],
  );

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: String(c.id), label: c.name })),
    [categories],
  );

  const sigOptions = useMemo(
    () => sigs.map((sig) => ({ value: String(sig.id), label: sig.name })),
    [sigs],
  );

  // An ownership filter or the All-assets toggle is itself a selection — the
  // backend reads either as "every asset (then scoped)", so the asset/category
  // pickers become optional and are disabled while All assets is on.
  const scopeIsAll = allAssets || ownershipType !== '' || owningGroup !== '';
  const pickersDisabled = loadingOptions || allAssets;
  const hasSelection =
    scopeIsAll || selectedAssetIds.length > 0 || selectedCategoryIds.length > 0;
  const periodComplete = periodMode !== 'custom' || Boolean(dateRange[0] && dateRange[1]);
  const canRun = hasSelection && periodComplete;

  const selectionHint =
    'Select at least one asset or category, switch on All assets, or pick an ownership scope — plus a complete period.';

  // Assemble the request params, or null if the selection/window is incomplete.
  const buildParams = useCallback((): CostRecoveryParams | null => {
    if (!hasSelection) return null;
    const base: CostRecoveryParams = {
      // All assets ignores the pickers (the backend widens the base set), so
      // don't send a stale id list alongside it.
      asset_ids: allAssets ? [] : selectedAssetIds,
      category_ids: allAssets ? [] : selectedCategoryIds.map(Number),
    };
    if (allAssets) {
      base.all_assets = true;
    }
    if (ownershipType !== '') {
      base.ownership_type = ownershipType as CostRecoveryOwnershipType;
    }
    if (owningGroup !== '') {
      base.owning_group = Number(owningGroup);
    }
    if (periodMode === 'custom') {
      if (!dateRange[0] || !dateRange[1]) return null;
      return {
        ...base,
        start_date: dayjs(dateRange[0]).format('YYYY-MM-DD'),
        end_date: dayjs(dateRange[1]).format('YYYY-MM-DD'),
      };
    }
    return { ...base, period: periodMode };
  }, [
    hasSelection,
    selectedAssetIds,
    selectedCategoryIds,
    allAssets,
    ownershipType,
    owningGroup,
    periodMode,
    dateRange,
  ]);

  const handleGenerate = async () => {
    const params = buildParams();
    if (!params) {
      setError(selectionHint);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await reportsAPI.getAssetCostRecovery(params);
      setReport(response.data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to generate the cost-recovery report.'));
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'csv' | 'pdf') => {
    const params = buildParams();
    if (!params) {
      setError(selectionHint);
      return;
    }
    try {
      setExporting(format);
      setError(null);
      const response = await reportsAPI.downloadAssetCostRecovery(params, format);
      const mime = format === 'pdf' ? 'application/pdf' : 'text/csv';
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `asset_cost_recovery.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(extractErrorMessage(err, `Failed to export ${format.toUpperCase()}.`));
    } finally {
      setExporting(null);
    }
  };

  return (
    <Stack gap="md" p="md">
      <Title order={2}>Asset Cost-Recovery Report</Title>
      <Text size="sm" c="dimmed">
        Build a per-asset service statement billed to the landlord. Scope it to
        specific assets or categories, to every asset, or to an ownership group
        (Space-owned, or one committee&apos;s assets). The Actual column is the
        recoverable amount: vendor invoices and recorded actuals on every asset,
        plus in-house repair cost <strong>only on assets flagged
        cost-recoverable</strong>. In-house cost is shown for every asset as
        context — on an unflagged asset it stays internal and is never billed.
      </Text>

      <Paper withBorder p="md">
        <Stack gap="md">
          <Switch
            label="All assets"
            description="Run the statement across every asset — the asset and category pickers are then ignored."
            checked={allAssets}
            onChange={(e) => setAllAssets(e.currentTarget.checked)}
            data-testid="cost-recovery-all-assets"
          />
          <MultiSelect
            label="Assets"
            placeholder="Search assets"
            data={assetOptions}
            value={selectedAssetIds}
            onChange={setSelectedAssetIds}
            searchable
            clearable
            disabled={pickersDisabled}
            data-testid="cost-recovery-assets"
          />
          <MultiSelect
            label="Categories"
            description="A category expands to all of its assets."
            placeholder="Search categories"
            data={categoryOptions}
            value={selectedCategoryIds}
            onChange={setSelectedCategoryIds}
            searchable
            clearable
            disabled={pickersDisabled}
            data-testid="cost-recovery-categories"
          />

          <Group grow align="flex-start">
            <Select
              label="Ownership type"
              description="Scopes to assets with that ownership."
              placeholder="Any"
              data={OWNERSHIP_OPTIONS}
              value={ownershipType}
              onChange={(value) => setOwnershipType(value ?? '')}
              data-testid="cost-recovery-ownership-type"
            />
            <Select
              label="Owning group (Committee / SIG)"
              description="Scopes to that committee's assets."
              placeholder="Any"
              data={sigOptions}
              value={owningGroup === '' ? null : owningGroup}
              onChange={(value) => setOwningGroup(value ?? '')}
              searchable
              clearable
              disabled={loadingOptions}
              data-testid="cost-recovery-owning-group"
            />
          </Group>

          <div>
            <Text size="sm" fw={500} mb={4}>
              Period
            </Text>
            <SegmentedControl
              value={periodMode}
              onChange={(value) => setPeriodMode(value as PeriodMode)}
              data={PERIOD_OPTIONS}
            />
          </div>

          {periodMode === 'custom' && (
            <DatePickerInput
              type="range"
              label="Date range"
              placeholder="Select start and end dates"
              value={dateRange}
              onChange={setDateRange}
              allowSingleDateInRange
              clearable
              maxDate={new Date()}
              style={{ maxWidth: 320 }}
            />
          )}

          <Group>
            <Button onClick={handleGenerate} loading={loading} disabled={!canRun}>
              Generate
            </Button>
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={() => handleExport('csv')}
              loading={exporting === 'csv'}
              disabled={!canRun}
            >
              Export CSV
            </Button>
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={() => handleExport('pdf')}
              loading={exporting === 'pdf'}
              disabled={!canRun}
            >
              Export PDF
            </Button>
          </Group>
        </Stack>
      </Paper>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Report error">
          {error}
        </Alert>
      )}

      {loading ? (
        <Group gap="xs">
          <Loader size="sm" />
          <Text>Generating statement…</Text>
        </Group>
      ) : report === null ? (
        <Text c="dimmed">
          Choose assets or categories — or switch on All assets, or pick an ownership
          scope — plus a period, then Generate the statement.
        </Text>
      ) : report.assets.length === 0 ? (
        <Paper withBorder p="md">
          <Text c="dimmed">No assets matched your selection for this period.</Text>
        </Paper>
      ) : (
        <Stack gap="md" data-testid="cost-recovery-results">
          <Paper withBorder p="md">
            <Group justify="space-between" wrap="wrap">
              <div>
                <Text fw={700} size="lg">
                  Grand total recoverable: ${formatMoney(report.grand_total_actual)}
                </Text>
                <Text size="sm" c="dimmed" data-testid="cost-recovery-grand-internal">
                  In-house cost across the selection: $
                  {formatMoney(report.grand_total_internal ?? '0')} — billable only on
                  assets flagged cost-recoverable. Estimated context: $
                  {formatMoney(report.grand_total_estimated)}
                </Text>
              </div>
              <Text size="sm" c="dimmed">
                {report.asset_count} asset{report.asset_count === 1 ? '' : 's'} ·{' '}
                {report.service_count} service{report.service_count === 1 ? '' : 's'} ·{' '}
                {describeWindow(report)}
              </Text>
            </Group>
          </Paper>

          {report.assets.map((asset) => (
            <Paper key={asset.asset_id} withBorder p="md">
              <Group justify="space-between" mb="xs" wrap="wrap">
                <div>
                  <Group gap="xs">
                    <Text fw={600}>
                      {asset.name}
                      {asset.asset_tag ? ` — ${asset.asset_tag}` : ''}
                    </Text>
                    {/* Whether in-house work on this asset is billable at all. */}
                    <Badge
                      color={asset.is_cost_recoverable ? 'green' : 'gray'}
                      variant="light"
                      data-testid={`cost-recoverable-badge-${asset.asset_id}`}
                    >
                      {asset.is_cost_recoverable
                        ? 'Cost-recoverable'
                        : 'In-house cost not billable'}
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed">
                    Serial: {asset.serial_number || '—'} · Installed:{' '}
                    {asset.date_received ? dayjs(asset.date_received).format('YYYY-MM-DD') : '—'} ·
                    Status: {asset.status_display}
                    {asset.category ? ` · ${asset.category}` : ''}
                  </Text>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Text fw={600}>Recoverable: ${formatMoney(asset.subtotal_actual)}</Text>
                  <Text size="sm" c="dimmed">
                    In-house cost: ${formatMoney(asset.subtotal_internal ?? '0')}
                    {asset.is_cost_recoverable ? '' : ' (internal only)'}
                  </Text>
                </div>
              </Group>
              <Table.ScrollContainer minWidth={640}>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Date</Table.Th>
                      <Table.Th>Source</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th ta="right">Estimated</Table.Th>
                      <Table.Th ta="right">In-house cost</Table.Th>
                      <Table.Th ta="right">Actual (recoverable)</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {asset.services.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={6} ta="center">
                          <Text c="dimmed">No services in this period</Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      asset.services.map((svc, idx) => (
                        <Table.Tr key={idx}>
                          <Table.Td>{dayjs(svc.date).format('YYYY-MM-DD')}</Table.Td>
                          <Table.Td>{SOURCE_LABELS[svc.source] ?? svc.source}</Table.Td>
                          <Table.Td>{svc.description || '—'}</Table.Td>
                          <Table.Td ta="right">
                            {svc.estimated_cost != null ? `$${formatMoney(svc.estimated_cost)}` : '—'}
                          </Table.Td>
                          <Table.Td ta="right">
                            {svc.internal_cost != null ? `$${formatMoney(svc.internal_cost)}` : '—'}
                          </Table.Td>
                          <Table.Td ta="right">
                            {svc.actual_cost != null ? `$${formatMoney(svc.actual_cost)}` : '—'}
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                  <Table.Tfoot>
                    <Table.Tr>
                      <Table.Th colSpan={3} ta="right">
                        Subtotal
                      </Table.Th>
                      <Table.Th ta="right">${formatMoney(asset.subtotal_estimated)}</Table.Th>
                      <Table.Th ta="right">
                        ${formatMoney(asset.subtotal_internal ?? '0')}
                      </Table.Th>
                      <Table.Th ta="right">${formatMoney(asset.subtotal_actual)}</Table.Th>
                    </Table.Tr>
                  </Table.Tfoot>
                </Table>
              </Table.ScrollContainer>
            </Paper>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default AssetCostRecoveryPage;
